/**
 * web/screens/settings.ts
 * Settings screen — controls / display / developer toggles.
 *
 * Wires `<input type="checkbox">` rows to {@link getUserSetting} /
 * {@link setUserSetting} and re-reads on `subscribeUserSettings` so the
 * row state reflects external mutations (e.g. another screen flipping a
 * dev toggle).
 *
 * @module web/screens/settings
 */

/// <reference lib="dom" />

import type { Route } from '../router';
import { injectStyles } from '../styles';
import {
  getUserSetting,
  setUserSetting,
  subscribeUserSettings,
  type UserSettings,
} from '@src/game/services/userSettings';

interface ToggleSpec {
  key: keyof UserSettings;
  label: string;
  hint: string;
}

const SECTIONS: { title: string; toggles: ToggleSpec[] }[] = [
  {
    title: 'Controls',
    toggles: [
      {
        key: 'showTouchControls',
        label: 'Show on-screen controls',
        hint: 'D-pad and action buttons. Default on for touch devices.',
      },
    ],
  },
  {
    title: 'Display',
    toggles: [
      {
        key: 'useGreyboxMode',
        label: 'Greybox mode',
        hint: 'Classic prototype visuals — coloured rectangles instead of sprites.',
      },
    ],
  },
  {
    title: 'Developer',
    toggles: [
      {
        key: 'devUnlockWorld2',
        label: 'Unlock World 2 (dev)',
        hint: 'Makes Starfield Drift selectable before the real unlock gate ships.',
      },
      {
        key: 'devUnlockWorld3',
        label: 'Unlock World 3 + double jump (dev)',
        hint: 'Surfaces Brasscloud Foundry on the world map and starts Nova with the double-jump ability before clearing the World 2 boss.',
      },
      {
        key: 'devUnlockWorld4',
        label: 'Unlock wall slide (dev)',
        hint: 'Starts Nova with the wall-slide ability before World 4.',
      },
      {
        key: 'devUseGemShard',
        label: 'Use Gem Shard projectile (dev)',
        hint: 'Fires the faster Gem Shard variant instead of Star Shards.',
      },
    ],
  },
];

const buildRow = (spec: ToggleSpec, allBoxes: HTMLInputElement[]): HTMLDivElement => {
  const row = document.createElement('div');
  row.className = 'sf-toggle-row';
  const text = document.createElement('div');
  // DECISION: pair label and checkbox via id/htmlFor so screen readers
  // announce them together and clicking the label text toggles the box.
  const checkboxId = `sf-setting-${spec.key}`;
  const label = document.createElement('label');
  label.htmlFor = checkboxId;
  label.textContent = spec.label;
  const hint = document.createElement('p');
  hint.style.margin = '0';
  hint.style.fontSize = '0.85rem';
  hint.style.color = 'var(--color-steel)';
  hint.textContent = spec.hint;
  text.append(label, hint);
  const box = document.createElement('input');
  box.id = checkboxId;
  box.type = 'checkbox';
  box.checked = getUserSetting(spec.key) as boolean;
  box.dataset['settingKey'] = spec.key;
  box.addEventListener('change', () => {
    setUserSetting(spec.key, box.checked as UserSettings[typeof spec.key]);
  });
  allBoxes.push(box);
  row.append(text, box);
  return row;
};

/** Settings route factory. */
export const settingsScreen: Route = (router) => {
  injectStyles();
  const screen = document.createElement('section');
  screen.className = 'sf-screen';
  screen.dataset['screen'] = 'settings';

  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'sf-btn sf-btn-back';
  back.textContent = '← Back';
  back.addEventListener('click', () => router.back('/'));

  const heading = document.createElement('h1');
  heading.className = 'sf-screen-title';
  heading.textContent = 'SETTINGS';

  const allBoxes: HTMLInputElement[] = [];
  const sectionEls: HTMLElement[] = [];
  for (const section of SECTIONS) {
    const wrap = document.createElement('div');
    wrap.className = 'sf-settings-section';
    const h3 = document.createElement('h3');
    h3.textContent = section.title;
    wrap.append(h3);
    for (const spec of section.toggles) wrap.append(buildRow(spec, allBoxes));
    sectionEls.push(wrap);
  }

  screen.append(back, heading, ...sectionEls);

  const unsubscribe = subscribeUserSettings(() => {
    for (const box of allBoxes) {
      const key = box.dataset['settingKey'] as keyof UserSettings | undefined;
      if (!key) continue;
      box.checked = getUserSetting(key) as boolean;
    }
  });

  const root = document.getElementById('app');
  root?.append(screen);
  return {
    destroy: () => {
      unsubscribe();
      screen.remove();
    },
  };
};
